import { useCallback, useEffect, useMemo, useRef } from 'react'

/**
 * Janela Document Picture-in-Picture genérica (Chrome/Edge 116+): abre uma janela sempre por cima e
 * MOVE para ela um nó DOM existente — sem recriá-lo, então o que estiver tocando continua tocando.
 * Não conhece a feature que a usa: quem chama decide qual é o nó e qual CSS extra a janela recebe.
 */

/** Base do documento da janela: sem margem, ocupando a janela inteira. */
const REGRAS_BASE = 'html, body { margin: 0; width: 100%; height: 100%; overflow: hidden; }'

export type OpcoesJanelaPip = {
  largura: number
  altura: number
  /** CSS aplicado só no documento da janela PiP. */
  regrasExtras?: string
  /**
   * Esconde o botão nativo "voltar à aba" (Chrome 124+): ele fecha a janela como o X, sem como diferenciar.
   * Quem usa oferece o próprio botão de retorno dentro do nó.
   */
  ocultarVoltarParaAba?: boolean
}

export type JanelaPip = {
  suportado: boolean
  /** Deve ser chamado dentro de um gesto do usuário. Resolve `false` se o navegador recusar. */
  abrir: (no: HTMLElement, opcoes: OpcoesJanelaPip) => Promise<boolean>
  /** Fecha SEM chamar `onFechar` (quem chama já sabe que fechou); o nó volta desanexado ao documento da aba. */
  fechar: () => void
  aberta: () => boolean
}

type Params = {
  /**
   * A janela fechou por ação do usuário (X, "voltar à aba", Esc) ou do navegador. Recebe o nó que estava nela,
   * já devolvido (desanexado) ao documento da aba; quem chama decide onde recolocá-lo.
   */
  onFechar: (no: HTMLElement) => void
}

/** Clonar nós é mais barato que serializar `cssRules` de todo o CSS do tema dentro do gesto do clique. */
const clonarEstilos = (origem: Document, destino: Document) => {
  origem.head.querySelectorAll('link[rel="stylesheet"], style').forEach((no) => {
    destino.head.appendChild(no.cloneNode(true))
  })
  // Atributos do <html> carregam tema (`data-bs-theme`) e variáveis de marca injetadas em runtime
  // (white-label). Mudanças feitas depois de abrir a janela não são sincronizadas.
  Array.from(origem.documentElement.attributes).forEach(({ name, value }) => {
    destino.documentElement.setAttribute(name, value)
  })
}

/**
 * Tira o nó do documento da janela e o devolve, desanexado, ao documento da aba. Obrigatório antes de a janela
 * ser descartada: o Chrome remove os listeners dos nós que ficam no documento descartado — inclusive os que o
 * React registra no container de um portal e não registra de novo —, e o nó voltaria "surdo" a cliques.
 */
const devolverAoDocumento = (no: HTMLElement) => {
  if (no.ownerDocument !== document) document.adoptNode(no)
}

const injetarRegras = (doc: Document, css: string) => {
  const style = doc.createElement('style')
  style.textContent = css
  doc.head.appendChild(style)
}

export const useJanelaPip = ({ onFechar }: Params): JanelaPip => {
  const janelaRef = useRef<Window | null>(null)
  const noRef = useRef<HTMLElement | null>(null)
  const onFecharRef = useRef(onFechar)

  useEffect(() => {
    onFecharRef.current = onFechar
  }, [onFechar])

  const fechar = useCallback(() => {
    const janela = janelaRef.current
    const no = noRef.current
    janelaRef.current = null
    noRef.current = null
    if (no) devolverAoDocumento(no)
    janela?.close()
  }, [])

  const abrir = useCallback(
    async (
      no: HTMLElement,
      { largura, altura, regrasExtras, ocultarVoltarParaAba }: OpcoesJanelaPip
    ) => {
      const api = window.documentPictureInPicture
      if (!api) return false

      if (janelaRef.current) {
        janelaRef.current.document.body.appendChild(no)
        noRef.current = no
        return true
      }

      try {
        // `requestWindow` roda antes de qualquer `await`: precisa do gesto do usuário ainda ativo.
        const janela = await api.requestWindow({
          width: largura,
          height: altura,
          ...(ocultarVoltarParaAba && { disallowReturnToOpener: true }),
        })
        clonarEstilos(document, janela.document)
        injetarRegras(janela.document, `${REGRAS_BASE}\n${regrasExtras ?? ''}`)
        janela.document.body.appendChild(no)
        janelaRef.current = janela
        noRef.current = no

        janela.document.addEventListener('keydown', (evento) => {
          if (evento.key === 'Escape') janela.close()
        })
        janela.addEventListener(
          'pagehide',
          () => {
            const atual = noRef.current
            janelaRef.current = null
            noRef.current = null
            if (!atual) return
            devolverAoDocumento(atual)
            onFecharRef.current(atual)
          },
          { once: true }
        )
        return true
      } catch {
        // Recusado (gesto expirado, política do site): quem chama mantém o fallback.
        return false
      }
    },
    []
  )

  const aberta = useCallback(() => janelaRef.current !== null, [])

  useEffect(() => fechar, [fechar])

  return useMemo(
    () => ({ suportado: Boolean(window.documentPictureInPicture), abrir, fechar, aberta }),
    [abrir, fechar, aberta]
  )
}
